import type { UIEvent } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { useFilteredListState } from '@frameio/vapor';
import { usePrevious, useThrottleCallback } from '@frameio/vapor';
import type { QuickSearchFilters } from '../../../types';

const SCROLL_THROTTLE = 100;
/** Tolerance in pixels for scroll position comparisons to handle browser rounding */
const SCROLL_POSITION_TOLERANCE_PX = 2;

export type QuickSearchListState = ReturnType<
  typeof useFilteredListState
>['state'];

type PaneIds = {
  list: string;
  detail: string;
  initial: string;
};

type UseQuickSearchScrollManagerParams = {
  listState: QuickSearchListState;
  hasCollectionSettled: boolean;
  handleOnScroll: (scrollTop: number, paneId?: string) => void;
  hasAnyPaneScrolled: boolean;
  resetPaneScrollState: () => void;
  addRecentSearch: (query: string) => void;
  selectListItemFromListKey: (key?: string | number | null) => void;
  getFirstListItemKey: () => string | number | null;
  focusInput: () => void;
  paneIds: PaneIds;
  /**
   * Whether the modal is in initial mount state with preserved results.
   * When true, skip ongoing search scroll management to allow initial
   * state restoration to handle scroll positioning.
   */
  isInitial?: boolean;
  /**
   * Current search filters. Used to detect filter changes and reset selection.
   */
  filters?: QuickSearchFilters;
};

type UseQuickSearchScrollManagerReturn = {
  handleOnListScroll: (evt: UIEvent<HTMLDivElement>) => void;
  handleOnDetailScroll: (evt: UIEvent<HTMLDivElement>) => void;
  handleOnInitialScroll: (evt: UIEvent<HTMLDivElement>) => void;
  immediateQuery: string;
};

/** Safely extracts scrollTop value from a scroll event. */
function getScrollTopValue(evt: UIEvent<HTMLDivElement>) {
  const target = (evt.currentTarget || evt.target) as HTMLDivElement | null;
  return target?.scrollTop;
}

/**
 * Manages scroll behavior and auto-selection for QuickSearch list.
 *
 * Handles throttled scroll events, auto-selection when results load, scroll position
 * preservation as lexical and NLP responses arrive sequentially, and query change resets.
 *
 * ## Ref-Based State Management
 *
 * Uses refs instead of state to avoid re-renders on frequent scroll events and prevent
 * infinite update loops. Each ref tracks specific behavior:
 *
 * - `listScrollContainerRef` - DOM ref for imperative scroll control
 * - `lastScrollResetQueryRef` - Prevents duplicate scroll resets per query
 * - `lastAutoSelectQueryRef` - Prevents duplicate auto-selections across follow-up batches
 * - `lastManualScrollTopRef` - Preserves user scroll position during updates
 * - `hasManualScrollForCurrentQueryRef` - Distinguishes user vs programmatic scroll
 * - `hasFocusedOnEmptyQueryRef` - Ensures input focused once per empty state
 * - `hasClearedEmptyQueryRef` - Prevents duplicate cleanup on query clear
 *
 * ### Flow
 * 1. Query typed → Scroll resets once
 * 2. Results load → Auto-select first (if not scrolled)
 * 3. User scrolls → Position saved
 * 4. More results → Position restored (prevents snap to top)
 * 5. Query cleared → All refs reset
 */
export function useQuickSearchScrollManager({
  listState,
  hasCollectionSettled,
  handleOnScroll,
  hasAnyPaneScrolled,
  resetPaneScrollState,
  addRecentSearch,
  selectListItemFromListKey,
  getFirstListItemKey,
  focusInput,
  paneIds,
  isInitial = false,
  filters,
}: UseQuickSearchScrollManagerParams): UseQuickSearchScrollManagerReturn {
  const listScrollContainerRef = useRef<HTMLDivElement>(null);
  const lastScrollResetQueryRef = useRef<string>('');
  const lastAutoSelectQueryRef = useRef<string>('');
  const lastManualScrollTopRef = useRef(0);
  const hasManualScrollForCurrentQueryRef = useRef(false);
  const hasFocusedOnEmptyQueryRef = useRef(false);
  const hasClearedEmptyQueryRef = useRef(false);
  // Tracks when we're programmatically selecting an item to distinguish from user scroll
  const isAutoSelectingRef = useRef(false);
  // Track which filter combination we've auto-selected for
  const lastAutoSelectFiltersRef = useRef<string>('');

  const immediateQuery = listState.queryState.query;
  const prevImmediateQuery = usePrevious(immediateQuery);

  // Create a stable string representation of filters for comparison
  const filtersKey = filters ? JSON.stringify(filters) : '';

  // Capture the active scroll container so we can restore positions when follow-up
  // result batches (e.g., NLP) arrive and prevent snap-back to the top.
  const [handleOnListScroll] = useThrottleCallback(
    (evt: UIEvent<HTMLDivElement>) => {
      const target = (evt.currentTarget || evt.target) as HTMLDivElement | null;
      if (target && listScrollContainerRef.current !== target) {
        listScrollContainerRef.current = target;
      }

      const scrollTop = getScrollTopValue(evt);
      if (scrollTop !== undefined) {
        handleOnScroll(scrollTop, paneIds.list);
        // Only track as manual scroll if not triggered by auto-select
        if (!isAutoSelectingRef.current) {
          lastManualScrollTopRef.current = scrollTop;
          hasManualScrollForCurrentQueryRef.current = scrollTop > 0;
        }
      }
    },
    SCROLL_THROTTLE
  );

  /** Creates a throttled scroll handler for non-list panes (detail, initial). */
  const createPaneScrollHandler = useCallback(
    (paneId: string) => (evt: UIEvent<HTMLDivElement>) => {
      const scrollTop = getScrollTopValue(evt);
      if (scrollTop !== undefined) {
        handleOnScroll(scrollTop, paneId);
      }
    },
    [handleOnScroll]
  );

  const [handleOnDetailScroll] = useThrottleCallback(
    createPaneScrollHandler(paneIds.detail),
    SCROLL_THROTTLE
  );

  const [handleOnInitialScroll] = useThrottleCallback(
    createPaneScrollHandler(paneIds.initial),
    SCROLL_THROTTLE
  );

  useLayoutEffect(() => {
    if (isInitial) return undefined;

    const trimmedQuery = listState.queryState.query.trim();
    const hasUserQuery = trimmedQuery !== '';

    if (!hasUserQuery) {
      // With no query we want the input ready for typing, but only focus once per
      // empty state to avoid triggering React's update depth guard.
      if (!hasFocusedOnEmptyQueryRef.current) {
        focusInput();
        hasFocusedOnEmptyQueryRef.current = true;
      }
      return undefined;
    }

    hasFocusedOnEmptyQueryRef.current = false;

    const hasFinishedInitialLoad =
      listState.prevLoadingVisible && !listState.isLoadingVisible;
    const isStreamingUpdate = !hasCollectionSettled;
    const shouldAutoSelect = hasFinishedInitialLoad || isStreamingUpdate;

    if (!shouldAutoSelect) return undefined;

    // Check if we've already auto-selected for this query+filter combination
    const haveFiltersChanged = filtersKey !== lastAutoSelectFiltersRef.current;
    const hasAutoSelectedForQuery =
      hasUserQuery &&
      lastAutoSelectQueryRef.current === trimmedQuery &&
      !haveFiltersChanged;

    if (hasAutoSelectedForQuery || hasAnyPaneScrolled) return undefined;

    // Ensure collection has items before selecting
    if (listState.collection.size === 0) return undefined;

    const firstListItemKey = getFirstListItemKey();
    if (!firstListItemKey) return undefined;

    if (hasUserQuery && !hasAutoSelectedForQuery) {
      addRecentSearch(trimmedQuery);
    }

    // Mark that we're auto-selecting to ignore Vapor's programmatic scroll
    isAutoSelectingRef.current = true;
    selectListItemFromListKey(firstListItemKey);
    lastAutoSelectQueryRef.current = trimmedQuery;
    lastAutoSelectFiltersRef.current = filtersKey;

    // Reset scroll to top after Vapor's scrollIntoView and clear auto-select flag.
    // Capture query at schedule time to detect stale executions.
    const queryAtSchedule = trimmedQuery;
    requestAnimationFrame(() => {
      // Guard: only execute if query hasn't changed since RAF was scheduled
      if (lastAutoSelectQueryRef.current !== queryAtSchedule) {
        isAutoSelectingRef.current = false;
        return;
      }
      if (listScrollContainerRef.current) {
        listScrollContainerRef.current.scrollTop = 0;
      }
      isAutoSelectingRef.current = false;
    });

    return undefined;
  }, [
    addRecentSearch,
    filtersKey,
    focusInput,
    getFirstListItemKey,
    hasAnyPaneScrolled,
    hasCollectionSettled,
    isInitial,
    listState.collection.size,
    listState.isLoadingVisible,
    listState.prevLoadingVisible,
    listState.queryState.query,
    selectListItemFromListKey,
  ]);

  // Reset scroll position once per query to prevent snap-back on follow-up batches.
  useLayoutEffect(() => {
    const hasQueryChanged = prevImmediateQuery !== immediateQuery;

    // Skip if no query change, but allow reset during isInitial when query changes
    if (isInitial && !hasQueryChanged) return;

    if (
      hasQueryChanged &&
      immediateQuery.trim() !== '' &&
      lastScrollResetQueryRef.current !== immediateQuery
    ) {
      if (listScrollContainerRef.current) {
        listScrollContainerRef.current.scrollTop = 0;
      }
      if (hasAnyPaneScrolled) {
        resetPaneScrollState();
      }
      lastScrollResetQueryRef.current = immediateQuery;
      lastManualScrollTopRef.current = 0;
      hasManualScrollForCurrentQueryRef.current = false;
    }
  }, [
    hasAnyPaneScrolled,
    immediateQuery,
    isInitial,
    prevImmediateQuery,
    resetPaneScrollState,
  ]);

  // Restore user's scroll position when additional results arrive (e.g., NLP)
  useLayoutEffect(() => {
    if (isInitial) return;

    // Skip scroll restoration when query just changed - the skeleton is rendering
    // and we want scroll at 0, not restored to the previous position
    if (prevImmediateQuery !== immediateQuery) return;

    const container = listScrollContainerRef.current;
    if (
      !container ||
      !hasManualScrollForCurrentQueryRef.current ||
      lastManualScrollTopRef.current <= 0
    ) {
      return;
    }

    const maxScrollTop = container.scrollHeight - container.clientHeight;
    if (maxScrollTop <= 0) return;

    const clampedScrollTop = Math.min(
      lastManualScrollTopRef.current,
      maxScrollTop
    );

    if (
      Math.abs(container.scrollTop - clampedScrollTop) <=
      SCROLL_POSITION_TOLERANCE_PX
    ) {
      return;
    }

    container.scrollTop = clampedScrollTop;
    lastManualScrollTopRef.current = container.scrollTop;
  }, [
    isInitial,
    listState.collection.size,
    prevImmediateQuery,
    immediateQuery,
  ]);

  // Reset bookkeeping refs on query or filter changes
  useEffect(() => {
    if (isInitial) return;

    const hasQueryChanged = prevImmediateQuery !== immediateQuery;
    const haveFiltersChanged = filtersKey !== lastAutoSelectFiltersRef.current;

    if (hasQueryChanged || haveFiltersChanged) {
      lastManualScrollTopRef.current = 0;
      hasManualScrollForCurrentQueryRef.current = false;
      // Reset auto-select query ref when query changes
      if (hasQueryChanged) {
        lastAutoSelectQueryRef.current = '';
      }
    }

    const trimmedImmediateQuery = immediateQuery.trim();
    if (trimmedImmediateQuery === '') {
      if (hasClearedEmptyQueryRef.current) {
        return;
      }

      hasClearedEmptyQueryRef.current = true;
      lastScrollResetQueryRef.current = '';
      lastAutoSelectQueryRef.current = '';
      lastAutoSelectFiltersRef.current = '';
      lastManualScrollTopRef.current = 0;
      hasManualScrollForCurrentQueryRef.current = false;
      resetPaneScrollState();
      return;
    }

    hasClearedEmptyQueryRef.current = false;
  }, [
    immediateQuery,
    isInitial,
    prevImmediateQuery,
    filtersKey,
    resetPaneScrollState,
  ]);

  return {
    handleOnListScroll,
    handleOnDetailScroll,
    handleOnInitialScroll,
    immediateQuery,
  };
}
